import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button, Tabs } from "@knowtex-ai/components";
import Sidebar from "../Sidebar";
import { getDoctorSpecialty } from "../Walkthrough";
import UploadMacrosPanel from "./SettingsMacros/UploadMacrosPanel";
import ManageMacrosPanel from "./SettingsMacros/ManageMacros";
import SharedDotPhrasesPanel from "./SettingsMacros/SharedDotPhrasesPanel";
import { MacrosContext } from "../../utils/MacrosContext";
import FullTutorialTrigger from "../Tutorial/FullTutorialTrigger";
import ModelTypeSelector from "./ModelTypeSelector";
import { getSessionToken } from "../../lib/sessionToken";

export default function Settings({ setAuth }) {
  const [userName, setUserName] = useState();
  const [userId, setUserId] = useState();
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [formattedSubEndDate, setFormattedSubEndDate] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [org, setOrg] = useState();
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [specialty, setSpecialty] = useState(null);
  const [superOrg, setSuperOrg] = useState(null);
  const [isIntegrated, setIsIntegrated] = useState("");
  const [modelType, setModelType] = useState(null);

  const [activeTab, setActiveTab] = useState("upload");


  // Fetch user info on component mount
  useEffect(() => {
    async function getUserInfo() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/userinfo`,
          {
            method: "GET",
            headers: { token: getSessionToken() },
          }
        );

        const parseRes = await response.json();
        setUserName(parseRes[0].user_name);
        setUserId(parseRes[0].user_id);
        setOrg(parseRes[0].organization);
        setSuperOrg(parseRes[0].super_org);
        setIsIntegrated(parseRes[0].ehr_password);
        setModelType(parseRes[0].model_type);
      } catch (err) {
        console.error("Error fetching user info:", err.message);
      }
    }
    getUserInfo();

    // Check if access token exists
    async function checkExistingIntegration() {
      try {
        const storedToken = await getStoredAccessToken();
        if (storedToken) {
          setAccessToken(storedToken.token);
        }
      } catch (error) {
        console.error("Error checking existing integration:", error);
      }
    }
    checkExistingIntegration();
  }, []);

  const handleOpenCancelClick = () => {
    setShowCancellationModal(true);
  };

  const handleOpenInformationClick = () => {
    setShowInformationModal(true);
  };

  useEffect(() => {
    if (subscriptionEndDate) {
      const options = { year: "numeric", month: "2-digit", day: "2-digit" };
      const formattedDate = new Date(subscriptionEndDate).toLocaleDateString(
        "en-US",
        options
      );
      setFormattedSubEndDate(formattedDate);
      localStorage.setItem("subscription_ends", formattedDate);
    }
  }, [subscriptionEndDate]);

  const getStoredAccessToken = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/getaccesstoken`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: getSessionToken(),
          },
        }
      );

      if (!response.ok) {
        // console.log("Can't get access token " + response);
        alert(
          "Unable to verify credentials to get access token. Please try again or contact Knowtex support to resolve this"
        );
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (data.accessToken) {
        //// console.log("Access token " + data.accessToken);
        return data.accessToken;
      } else {
        console.error("Access token not available.");
        return null;
      }
    } catch (error) {
      console.error("Failed to fetch access token:", error);
      return null;
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/userinfo`,
        {
          method: "GET",
          headers: { token: getSessionToken() },
        }
      );

      const parseRes = await response.json();
      setUserName(parseRes[0].user_name);
      setUserId(parseRes[0].user_id);
      setOrg(parseRes[0].organization);
      setSubscriptionId(parseRes[0].subscription_id);
      setSubscriptionEndDate(parseRes[0].subscription_end_date);
      setSuperOrg(parseRes[0].super_org);
      setIsIntegrated(parseRes[0].ehr_password);
      setModelType(parseRes[0].model_type);
    } catch (err) {
      console.error("Error fetching user info:", err.message);
    }
  };

  // Fetch doctor specialty on component mount
  useEffect(() => {
    async function fetchSpecialty() {
      const doctorSpecialty = await getDoctorSpecialty();
      setSpecialty(doctorSpecialty);
    }

    fetchSpecialty();
  }, []);

  return (
    <>
      <Helmet><title>Settings – Knowtex</title></Helmet>
      <Sidebar setAuth={setAuth} />
      <main id="main-content" tabIndex="-1" className="flex flex-col">
        <div className="flex flex-col transition-margin ml-sidebar-rail tablet:mt-3 desktop:ml-sidebar-open">
          <div className="flex mt-5 text-4xl font-bold text-accent1">
            <h1>Settings</h1>
          </div>
          <ModelTypeSelector currentModelType={modelType} />
          {/* Macros Section with Tabs */}
          <div id="add_new_dot_phrase" className="flex flex-col mt-5 mb-5">
            <h2 className="text-accent1 text-2xl font-bold ">Dot Phrases</h2>
            <div className="text-light-grey italic text-sm mb-4">
              View and manage your dot phrases.
            </div>

            {/* Hidden trigger for programmatic switch back to upload tab */}
            <button id="add_new_dot_phrase_button" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} onClick={() => setActiveTab("upload")} aria-hidden="true" tabIndex={-1} />

            {/* Tab Navigation — wrapper ID used by tutorial to highlight & target the "My Dot Phrases" tab */}
            <div id="manage_dot_phrase_button">
              <Tabs
                tabs={[
                  { id: "upload", label: "Upload Custom Dot Phrases", onClick: () => setActiveTab("upload") },
                  { id: "manage", label: "My Dot Phrases", onClick: () => setActiveTab("manage") },
                ]}
                activeTab={activeTab}
              />
            </div>

            {/* Tab Content */}
            <div className="max-w-4xl w-full">
              {activeTab === "upload" && <UploadMacrosPanel userId={userId} />}
              {activeTab === "manage" && (
                <>
                  <ManageMacrosPanel />
                  <SharedDotPhrasesPanel />
                </>
              )}
            </div>
          </div>
        </div>
        <FullTutorialTrigger />
      </main>
    </>
  );
}
